/**
 * Safe UUID generation utility
 * Provides fallback for environments where crypto.randomUUID is not available
 * (e.g., some Android WebViews, older browsers)
 */

/**
 * Generates a UUID v4 using crypto.getRandomValues
 * This is a reliable fallback when crypto.randomUUID is not available
 */
function generateUUIDv4(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  
  // Set version 4 (random) in the 7th byte
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  // Set variant (10xx) in the 9th byte
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  
  const hex = Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Math.random fallback - least preferred but works everywhere
 */
function generateUUIDFallback(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Safe UUID generator with fallbacks for all environments
 * Priority:
 * 1. crypto.randomUUID (fastest, most secure)
 * 2. crypto.getRandomValues (secure, widely supported)
 * 3. Math.random fallback (works everywhere)
 */
export function safeRandomUUID(): string {
  try {
    // Try native randomUUID first (fastest)
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    
    // Fallback to getRandomValues (widely supported)
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      return generateUUIDv4();
    }
    
    // Last resort: Math.random
    return generateUUIDFallback();
  } catch {
    // If anything fails, use Math.random fallback
    return generateUUIDFallback();
  }
}
